// per docs/ui-shape-role-first-dashboards.md §2/§5 — reading
// analytics.dashboard_config.role_scope from the role's side (caiet UC26-02,
// UC61-01/02/03).
//
// The membership rule itself is NOT restated here: roleSatisfiesGrant in
// @simsm/shared-types is the one predicate the analytics service, the BFF edge
// and this screen all apply, so a Specific Observer keeps reaching a grant
// addressed to the whole observer family. What this module adds is the
// distinction the administrator needs on screen: not just *whether* the role
// reaches a dashboard, but *how* it reaches it, because only one of the ways is
// a grant this role can be given or taken away. An empty role_scope reaches no
// role: it is "not shared", the System Administrator only
// (docs/shape-ops-dashboard-sharing.md §2).
import { roleSatisfiesGrant } from '@simsm/shared-types';

export type GrantKind =
  // role_scope names this role explicitly
  | 'specific'
  // role_scope names OBSERVER and this role is a Specific Observer (UC61-01/02)
  | 'observer-family'
  | 'none';

export function grantKindFor(roleScope: readonly string[], roleCode: string): GrantKind {
  if (roleScope.includes(roleCode)) return 'specific';
  if (roleScope.some((grant) => roleSatisfiesGrant(roleCode, grant))) return 'observer-family';
  return 'none';
}

// Why a revocation is refused, or null when it is allowed. Mirrors
// planRoleDashboardScopes in the analytics service: the UI refuses before the
// server has to, and says which screen owns the change instead.
export type RevokeBlock = 'observer-family' | 'not-granted' | null;

export function revokeBlockFor(roleScope: readonly string[], roleCode: string): RevokeBlock {
  const kind = grantKindFor(roleScope, roleCode);
  if (kind === 'observer-family') return 'observer-family';
  if (kind === 'none') return 'not-granted';
  // Dropping the last name empties the scope, which leaves the dashboard to the
  // System Administrator alone: a revocation like any other.
  return null;
}

// A role named alongside OBSERVER keeps the dashboard after its own name is
// removed. The confirmation has to say so, or the administrator reads a
// revocation that visibly changed nothing as a failure.
export function stillReachedAfterRevoke(roleScope: readonly string[], roleCode: string): boolean {
  return roleScope
    .filter((grant) => grant !== roleCode)
    .some((grant) => roleSatisfiesGrant(roleCode, grant));
}

export interface DuplicateIdentity {
  slug: string;
  title: string;
}

export interface TakenIdentities {
  slugs: readonly string[];
  titles: readonly string[];
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function firstFree(
  base: string,
  taken: readonly string[],
  compare: (value: string) => string,
  join: (base: string, attempt: number) => string,
): string {
  const used = new Set(taken.map(compare));
  if (!used.has(compare(base))) return base;
  for (let attempt = 2; attempt < 100; attempt += 1) {
    const next = join(base, attempt);
    if (!used.has(compare(next))) return next;
  }
  return join(base, Date.now());
}

// A duplicate carries the institution in both its address and its title, so the
// two rows are never mistaken for one another in the dashboard-first list. The
// slug has to satisfy the same ^[a-z0-9][a-z0-9-]{2,79}$ the create form checks.
export function duplicateIdentity(
  source: { slug: string; title: string },
  roleCode: string,
  roleLabel: string,
  taken: TakenIdentities,
): DuplicateIdentity {
  const suffix = slugify(roleCode.replace(/^OBSERVER_/, '')) || slugify(roleCode) || 'rol';
  const base = `${slugify(source.slug) || 'tablou'}-${suffix}`.slice(0, 79);
  return {
    slug: firstFree(
      base,
      taken.slugs,
      (v) => v,
      (v, n) => `${v}-${n}`,
    ),
    title: firstFree(
      `${source.title} (${roleLabel})`,
      taken.titles,
      (v) => v.trim().toLowerCase(),
      (v, n) => `${v} (${n})`,
    ),
  };
}
